import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SearchHistory from './SearchHistory';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiUser, FiMapPin, FiBriefcase, FiMail, FiPhone, FiLoader, FiSettings, FiKey, FiGlobe, FiCheckCircle, FiAlertTriangle } = FiIcons;

const SearchForm = ({ onSearch, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    company: '',
    email: '',
    phone: ''
  });
  const [showSettings, setShowSettings] = useState(false);
  const [apiConfig, setApiConfig] = useState({
    googleApiKey: 'AIzaSyCMmDZ74A24PBTOhLpax3ANSA7EsCQ13c8', // Pre-filled with provided key
    googleCx: '37d04164432e54910' // Pre-filled with provided CX
  });
  const historyRef = useRef(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleConfigChange = (e) => {
    setApiConfig({ ...apiConfig, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSearch(formData, apiConfig);
    }
  };

  const handleHistorySelect = (searchData) => {
    setFormData(searchData);
  };

  const inputFields = [
    { name: 'name', icon: FiUser, placeholder: 'Full Name (Required)', required: true },
    { name: 'location', icon: FiMapPin, placeholder: 'Location (Optional)' },
    { name: 'company', icon: FiBriefcase, placeholder: 'Company (Optional)' },
    { name: 'email', icon: FiMail, placeholder: 'Email (Optional)', type: 'email' },
    { name: 'phone', icon: FiPhone, placeholder: 'Phone (Optional)', type: 'tel' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Search History Component */}
      <SearchHistory 
        ref={historyRef} 
        onHistorySelect={handleHistorySelect} 
        isLoading={isLoading} 
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-purple-500/20"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Identity Verification Research
          </h2>
          <p className="text-purple-300 text-lg">
            Enter a person's details to analyze their <strong>real</strong> digital footprint.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {inputFields.map((field, index) => (
              <motion.div
                key={field.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400">
                  <SafeIcon icon={field.icon} className="text-xl" />
                </div>
                <input
                  type={field.type || 'text'}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleInputChange}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="w-full pl-12 pr-4 py-4 bg-black/30 border border-purple-500/30 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                />
              </motion.div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center space-x-2 text-sm text-purple-300 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiSettings} />
              <span>Advanced Search Settings</span>
              {(!apiConfig.googleApiKey || !apiConfig.googleCx) && (
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                </span>
              )}
            </button>
          </div>

          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-black/20 rounded-lg border border-purple-500/20 space-y-4">
                  <div className="flex justify-between items-start">
                    <p className="text-xs text-purple-300">
                      <strong>Configuration:</strong> Both the API Key and Search Engine ID (CX) are pre-filled. 
                      <br />Full web search is now enabled. Enhanced with Reddit, TikTok, and Instagram search.
                    </p>
                    <a 
                      href="https://programmablesearchengine.google.com/controlpanel/create" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-blue-400 hover:text-blue-300 underline flex items-center gap-1"
                    >
                      Manage CX <SafeIcon icon={FiGlobe} className="text-xs"/>
                    </a>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400">
                        <SafeIcon icon={FiKey} />
                      </div>
                      <input
                        type="password"
                        name="googleApiKey"
                        value={apiConfig.googleApiKey}
                        onChange={handleConfigChange}
                        placeholder="Google API Key"
                        className="w-full pl-10 pr-4 py-2 bg-black/40 border border-purple-500/30 rounded text-sm text-white focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400">
                        <SafeIcon icon={FiSearch} />
                      </div>
                      <input
                        type="text"
                        name="googleCx"
                        value={apiConfig.googleCx}
                        onChange={handleConfigChange}
                        placeholder="Search Engine ID (CX)"
                        className="w-full pl-10 pr-4 py-2 bg-black/40 border border-purple-500/30 rounded text-sm text-white focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {apiConfig.googleCx && apiConfig.googleApiKey ? (
                    <p className="text-xs text-green-500/80 italic flex items-center gap-2">
                      <SafeIcon icon={FiCheckCircle} />
                      Full web search is active! Using reliable sources: Gravatar + Reddit + Social Media + Image Search
                    </p>
                  ) : (
                    <p className="text-xs text-yellow-500/80 flex items-center gap-2">
                      <SafeIcon icon={FiAlertTriangle} />
                      Demo images will be shown without API keys.
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            disabled={isLoading || !formData.name.trim()}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-3"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <>
                <SafeIcon icon={FiLoader} className="text-xl animate-spin" />
                <span>Searching Live Sources...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSearch} className="text-xl" />
                <span>Start Real OSINT Research</span>
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-blue-300 text-sm text-center">
            <strong>Data Sources:</strong> This tool connects to reliable APIs (Gravatar, Reddit, TikTok, Instagram, LinkedIn, Twitter/X, Facebook, Google Search, Google Images). 
            Results are based on actual public records and social media activity.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SearchForm;